import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { supabase } from '@/integrations/supabase/client';
import { UnifiedCampaign } from '@/types/unifiedCampaign';
import { Phone, Clock, AlertCircle } from 'lucide-react';

interface CallLogRow {
  call_id: string;
  prospect_id: string | null;
  phone_number: string | null;
  status: string;
  outcome: string | null;
  duration_seconds: number | null;
  started_at: string | null;
  completed_at: string | null;
  notes: any;
}

interface CampaignReportModalProps {
  campaign: UnifiedCampaign | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const OUTCOME_LABELS: Record<string, string> = {
  booked: 'Booked',
  interested: 'Interested',
  engaged: 'Engaged',
  callback: 'Callback Requested',
  voicemail: 'Voicemail',
  no_answer: 'No Answer',
  objection: 'Not Interested',
  not_interested: 'Not Interested',
};

const OUTCOME_COLORS: Record<string, string> = {
  booked: 'bg-green-500/10 text-green-700 border-green-500/20',
  interested: 'bg-blue-500/10 text-blue-700 border-blue-500/20',
  engaged: 'bg-blue-500/10 text-blue-700 border-blue-500/20',
  callback: 'bg-yellow-500/10 text-yellow-700 border-yellow-500/20',
  voicemail: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
  no_answer: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
  objection: 'bg-red-500/10 text-red-700 border-red-500/20',
  not_interested: 'bg-red-500/10 text-red-700 border-red-500/20',
};

function formatDuration(seconds: number | null): string {
  if (!seconds || seconds === 0) return '0s';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function formatTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function CampaignReportModal({
  campaign,
  open,
  onOpenChange,
}: CampaignReportModalProps) {
  const [logs, setLogs] = useState<CallLogRow[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !campaign) return;

    const fetchLogs = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('call_logs')
          .select(
            'call_id, prospect_id, phone_number, status, outcome, duration_seconds, started_at, completed_at, notes'
          )
          .eq('campaign_id', campaign.id)
          .order('started_at', { ascending: false });

        if (error) throw error;
        setLogs(data || []);
      } catch (err) {
        console.error('Failed to fetch call logs:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [open, campaign]);

  if (!campaign) return null;

  // Summary counts
  const total = logs.length;
  const byOutcome = logs.reduce<Record<string, number>>((acc, log) => {
    const key = log.outcome || 'unknown';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const answered = logs.filter(
    (l) => l.duration_seconds && l.duration_seconds > 5
  ).length;
  const avgDuration =
    logs.length > 0
      ? Math.round(
          logs.reduce((sum, l) => sum + (l.duration_seconds || 0), 0) /
            logs.length
        )
      : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-3xl max-h-[85vh] flex flex-col'>
        <DialogHeader>
          <DialogTitle>After Action Report — {campaign.name}</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className='flex items-center justify-center py-12 text-muted-foreground'>
            Loading report...
          </div>
        ) : total === 0 ? (
          <div className='flex flex-col items-center justify-center py-12 text-muted-foreground gap-2'>
            <AlertCircle className='h-8 w-8' />
            <p>No calls recorded for this campaign yet.</p>
          </div>
        ) : (
          <>
            {/* Summary bar */}
            <div className='grid grid-cols-2 sm:grid-cols-4 gap-3 py-2'>
              <div className='rounded-lg border p-3 text-center'>
                <p className='text-2xl font-bold'>{total}</p>
                <p className='text-xs text-muted-foreground'>Total Calls</p>
              </div>
              <div className='rounded-lg border p-3 text-center'>
                <p className='text-2xl font-bold'>{answered}</p>
                <p className='text-xs text-muted-foreground'>Answered</p>
              </div>
              <div className='rounded-lg border p-3 text-center'>
                <p className='text-2xl font-bold'>
                  {byOutcome['booked'] || 0}
                </p>
                <p className='text-xs text-muted-foreground'>Booked</p>
              </div>
              <div className='rounded-lg border p-3 text-center'>
                <p className='text-2xl font-bold'>{formatDuration(avgDuration)}</p>
                <p className='text-xs text-muted-foreground'>Avg Duration</p>
              </div>
            </div>

            {/* Per-call table */}
            <ScrollArea className='flex-1 mt-2'>
              <div className='space-y-2 pr-2'>
                {logs.map((log) => {
                  const endedReason = log.notes?.callData?.endedReason;
                  return (
                    <div
                      key={log.call_id}
                      className='flex items-center justify-between rounded-lg border px-4 py-3 text-sm gap-4'
                    >
                      <div className='flex items-center gap-3 min-w-0'>
                        <Phone className='h-4 w-4 text-muted-foreground shrink-0' />
                        <div className='min-w-0'>
                          <span className='text-sm font-medium truncate'>
                            {log.phone_number || 'Unknown number'}
                          </span>
                          <div className='flex items-center gap-1 mt-0.5 text-xs text-muted-foreground'>
                            <Clock className='h-3 w-3' />
                            {formatTime(log.started_at)}
                            {endedReason && (
                              <span className='ml-2 text-muted-foreground/70'>
                                · {endedReason}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className='flex items-center gap-3 shrink-0'>
                        <span className='text-xs text-muted-foreground'>
                          {formatDuration(log.duration_seconds)}
                        </span>
                        {(() => {
                          const outcome = log.outcome ||
                            (endedReason === 'no-answer' || !log.duration_seconds || log.duration_seconds < 5
                              ? 'no_answer'
                              : endedReason === 'voicemail'
                              ? 'voicemail'
                              : null);
                          return (
                            <Badge
                              variant='outline'
                              className={
                                OUTCOME_COLORS[outcome || ''] ||
                                'bg-gray-500/10 text-gray-600 border-gray-500/20'
                              }
                            >
                              {OUTCOME_LABELS[outcome || ''] || outcome || 'No Outcome'}
                            </Badge>
                          );
                        })()}
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
